// PropertyHeroMap — May 7, 2026
//
// Mapbox embed at the top of the Property tab. Renders this property's pin
// in Bardin orange + a 1-mi orange dashed storm radius circle + NEXRAD
// radar hail signatures (blue circles, sized by max_size_in) + NWS spotter
// reports (red triangles) + other knock pins in the area as small grey dots
// for context.
//
// Data sources (all already in Supabase):
//   - storm signatures: canvas_storm_signatures_within_radius(lat, lon, m, since)
//   - NWS LSR:           canvas_nws_lsr_within_radius(lat, lon, m, since)
//   - other knock pins:  SELECT FROM knock_pins via earth_box bbox lookup
//
// Auto-fits to the union of property + 1-mi radius. Mapbox loaded via CDN
// (same as Canvas.tsx). Renders nothing if deal lacks property_lat/lon.

import { useEffect, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import type { Deal } from "../../lib/types";

declare const mapboxgl: any;

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;
const RADIUS_MILES = 1;
const RADIUS_METERS = RADIUS_MILES * 1609.344;
const SINCE_DAYS = 365;

interface StormSig { id: string; occurred_at: string; lat: number; lon: number; max_size_in: number; }
interface SpotterReport { id: string; occurred_at: string; lat: number; lon: number; report_type: string; magnitude: number | null; remarks: string | null; }
interface NeighborPin { id: string; lat: number; lon: number; current_state: string; }

export default function PropertyHeroMap({ deal }: { deal: Deal }) {
  const lat = (deal as any).property_lat ? Number((deal as any).property_lat) : null;
  const lon = (deal as any).property_lon ? Number((deal as any).property_lon) : null;
  const mapDiv = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  // Track DOM markers so we can clear them between effect re-runs (would
  // otherwise stack up extra property pins / spotter triangles on top of
  // the existing ones when knock_pin_id or deal data changes).
  const markersRef = useRef<any[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [stats, setStats] = useState<{ radar: number; spotters: number; neighbors: number } | null>(null);

  // ── Init map (once) ────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapDiv.current || mapRef.current) return;
    if (lat == null || lon == null) return;
    if (typeof mapboxgl === "undefined") {
      setErr("Mapbox library not loaded");
      return;
    }
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: mapDiv.current,
      style: "mapbox://styles/mapbox/satellite-streets-v12",
      center: [lon, lat],
      zoom: 14,
      attributionControl: false,
      cooperativeGestures: true, // require ctrl+scroll to zoom — keeps page scroll smooth
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), "bottom-right");
    mapRef.current = map;
    return () => {
      try { map.remove(); } catch { /* ignore */ }
      mapRef.current = null;
    };
  }, [lat, lon]);

  // ── Fetch storm/spotter/neighbor data + render layers ─────────────────
  useEffect(() => {
    if (lat == null || lon == null) return;
    const map = mapRef.current;
    if (!map) return;

    let cancelled = false;
    const since = new Date(Date.now() - SINCE_DAYS * 24 * 3600 * 1000).toISOString();

    (async () => {
      try {
        const [sigRes, lsrRes, pinRes] = await Promise.all([
          supabase.rpc("canvas_storm_signatures_within_radius",
            { p_lat: lat, p_lon: lon, p_radius_meters: RADIUS_METERS, p_since: since }),
          supabase.rpc("canvas_nws_lsr_within_radius",
            { p_lat: lat, p_lon: lon, p_radius_meters: RADIUS_METERS, p_since: since }),
          // Neighbor pins: use earth_box for fast bbox prefilter
          supabase.from("knock_pins")
            .select("id, lat, lon, current_state")
            .gte("lat", lat - 0.02)
            .lte("lat", lat + 0.02)
            .gte("lon", lon - 0.025)
            .lte("lon", lon + 0.025)
            .neq("id", (deal as any).knock_pin_id ?? "00000000-0000-0000-0000-000000000000")
            .limit(50),
        ]);
        if (cancelled) return;

        const sigs    = (sigRes.data    ?? []) as StormSig[];
        const spots   = (lsrRes.data    ?? []) as SpotterReport[];
        const neighbors = (pinRes.data ?? []) as NeighborPin[];
        setStats({ radar: sigs.length, spotters: spots.length, neighbors: neighbors.length });

        const apply = () => {
          // Clear previously-rendered DOM markers so re-runs don't stack
          for (const m of markersRef.current) {
            try { m.remove(); } catch { /* ignore */ }
          }
          markersRef.current = [];

          // 1-mi storm radius (orange dashed circle, faint fill)
          const ringFeature = makeCircleFeature([lon, lat], RADIUS_METERS, 64);
          if (!map.getSource("hero-radius")) {
            map.addSource("hero-radius", { type: "geojson", data: ringFeature });
            map.addLayer({
              id: "hero-radius-fill",
              type: "fill",
              source: "hero-radius",
              paint: { "fill-color": "#f46530", "fill-opacity": 0.06 },
            });
            map.addLayer({
              id: "hero-radius-line",
              type: "line",
              source: "hero-radius",
              paint: {
                "line-color": "#f46530",
                "line-width": 1.5,
                "line-dasharray": [3, 2],
                "line-opacity": 0.7,
              },
            });
          } else {
            map.getSource("hero-radius").setData(ringFeature);
          }

          // Neighbor knock pins — small grey dots
          const neighborGeo = {
            type: "FeatureCollection",
            features: neighbors.map((n) => ({
              type: "Feature",
              properties: { id: n.id, state: n.current_state },
              geometry: { type: "Point", coordinates: [Number(n.lon), Number(n.lat)] },
            })),
          };
          if (!map.getSource("hero-neighbors")) {
            map.addSource("hero-neighbors", { type: "geojson", data: neighborGeo });
            map.addLayer({
              id: "hero-neighbors-pts",
              type: "circle",
              source: "hero-neighbors",
              paint: {
                "circle-radius": 3.5,
                "circle-color": "#94a3b8",
                "circle-opacity": 0.75,
                "circle-stroke-color": "#ffffff",
                "circle-stroke-width": 1,
              },
            });
          } else {
            map.getSource("hero-neighbors").setData(neighborGeo);
          }

          // Radar signatures — blue circles sized by hail diameter
          const sigGeo = {
            type: "FeatureCollection",
            features: sigs.map((s) => ({
              type: "Feature",
              properties: {
                id: s.id, size: Number(s.max_size_in ?? 0),
                date: s.occurred_at?.slice(0, 10),
              },
              geometry: { type: "Point", coordinates: [Number(s.lon), Number(s.lat)] },
            })),
          };
          if (!map.getSource("hero-sigs")) {
            map.addSource("hero-sigs", { type: "geojson", data: sigGeo });
            map.addLayer({
              id: "hero-sigs-pts",
              type: "circle",
              source: "hero-sigs",
              paint: {
                // Radius interpolates with hail size: 1.0" → 5px, 2.5"+ → 14px
                "circle-radius": [
                  "interpolate", ["linear"], ["get", "size"],
                  0.5, 4, 1.0, 6, 1.5, 9, 2.0, 12, 3.0, 16,
                ],
                "circle-color": "#2563eb",
                "circle-opacity": 0.55,
                "circle-stroke-color": "#1e40af",
                "circle-stroke-width": 1,
              },
            });
            // Click → popup with size + date
            map.on("click", "hero-sigs-pts", (e: any) => {
              const p = e.features?.[0]?.properties ?? {};
              const c = e.features?.[0]?.geometry?.coordinates;
              if (!c) return;
              new mapboxgl.Popup()
                .setLngLat(c)
                .setHTML(`<div style="font-family:system-ui;font-size:12px"><strong>${Number(p.size).toFixed(2)}″ hail</strong><br/>${p.date}</div>`)
                .addTo(map);
            });
            map.on("mouseenter", "hero-sigs-pts", () => { map.getCanvas().style.cursor = "pointer"; });
            map.on("mouseleave", "hero-sigs-pts", () => { map.getCanvas().style.cursor = ""; });
          } else {
            map.getSource("hero-sigs").setData(sigGeo);
          }

          // NWS spotter reports — red triangle markers via DOM elements
          // (Mapbox doesn't ship a triangle symbol icon; SDF would need an
          // image; using DOM markers is simpler for a small handful.)
          for (const s of spots) {
            const el = document.createElement("div");
            el.style.cssText = `
              width: 0; height: 0;
              border-left: 7px solid transparent;
              border-right: 7px solid transparent;
              border-bottom: 12px solid #b94a3a;
              filter: drop-shadow(0 1px 2px rgba(0,0,0,0.4));
              cursor: pointer;
            `;
            el.title = `Spotter · ${s.report_type}${s.magnitude ? ` · ${s.magnitude}` : ""}${s.occurred_at ? ` · ${s.occurred_at.slice(0,10)}` : ""}${s.remarks ? `\n${s.remarks}` : ""}`;
            const spotterMk = new mapboxgl.Marker({ element: el, anchor: "bottom" })
              .setLngLat([Number(s.lon), Number(s.lat)])
              .addTo(map);
            markersRef.current.push(spotterMk);
          }

          // The property pin (Bardin orange, on top, with a halo)
          const propEl = document.createElement("div");
          propEl.style.cssText = `
            width: 22px; height: 22px; border-radius: 50%;
            background: #f46530; border: 3px solid white;
            box-shadow: 0 0 0 3px rgba(244,101,48,0.35), 0 1px 3px rgba(0,0,0,0.5);
          `;
          const propMk = new mapboxgl.Marker({ element: propEl })
            .setLngLat([lon, lat])
            .addTo(map);
          markersRef.current.push(propMk);

          // Fit map to the 1-mi radius (with a hair of padding)
          const ring = ringFeature.geometry.coordinates[0] as [number, number][];
          const bounds = ring.reduce(
            (b: any, c: [number, number]) => b.extend(c),
            new mapboxgl.LngLatBounds(ring[0], ring[0]),
          );
          map.fitBounds(bounds, { padding: 30, duration: 600, maxZoom: 16 });
        };

        if (map.isStyleLoaded()) apply();
        else map.once("style.load", apply);
      } catch (e: any) {
        if (!cancelled) setErr(e?.message ?? String(e));
      }
    })();

    return () => { cancelled = true; };
  }, [lat, lon, (deal as any).knock_pin_id]);

  if (lat == null || lon == null) return null; // no map without coords

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          🗺️ Storm activity within {RADIUS_MILES} mi · last {SINCE_DAYS} days
        </div>
        {stats ? (
          <div className="flex items-center gap-3 text-[10px] text-slate-500">
            <span><span className="inline-block h-2 w-2 rounded-full bg-blue-500/80 align-middle"/> {stats.radar} radar</span>
            <span><span className="inline-block h-0 w-0 align-middle" style={{borderLeft:"4px solid transparent",borderRight:"4px solid transparent",borderBottom:"6px solid #b94a3a"}}/> {stats.spotters} spotter</span>
            <span><span className="inline-block h-2 w-2 rounded-full bg-slate-400 align-middle"/> {stats.neighbors} pins</span>
          </div>
        ) : null}
      </div>
      <div ref={mapDiv} className="h-72 w-full" />
      {err ? (
        <div className="border-t border-red-200 bg-red-50 px-3 py-1.5 text-xs text-red-700">⚠ {err}</div>
      ) : null}
    </div>
  );
}

// Build a polygon ring approximating a circle of `meters` around [lon,lat].
// Uses a simple equirectangular projection — accurate enough at residential
// scales (sub-1km).
function makeCircleFeature(center: [number, number], meters: number, steps = 64) {
  const [lon, lat] = center;
  const dLat = meters / 111320;
  const dLon = meters / (111320 * Math.cos((lat * Math.PI) / 180));
  const ring: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const t = (i * 2 * Math.PI) / steps;
    ring.push([lon + Math.cos(t) * dLon, lat + Math.sin(t) * dLat]);
  }
  return {
    type: "Feature" as const,
    properties: {},
    geometry: { type: "Polygon" as const, coordinates: [ring] },
  };
}
